'use client';

import { ToggleGroup as BaseToggle, cn, ToggleGroupItem } from '@shared/components/shadcn';
import { forwardRef, type ReactNode } from 'react';
import { CheckboxDisplay } from './CheckBoxDisplay';

type ToggleOption = {
  value: string;
  label: string;
  icon: ReactNode;
  trackingProps?: Record<string, unknown>;
};

type ToggleGroupProps = {
  id?: string;
  value: string[];
  onValueChange: (value: string[]) => void;
  options: ToggleOption[];
  hasError?: boolean;
  className?: string;
};

const ToggleGroup = forwardRef<HTMLDivElement, ToggleGroupProps>(({ id, value, onValueChange, options, hasError, className, ...props }, ref) => {
  return (
    <BaseToggle
      {...props}
      ref={ref}
      id={id}
      type="multiple"
      variant="outline"
      value={value || []}
      onValueChange={onValueChange}
      className={cn('flex flex-col gap-3 w-full !m-0 !p-0 !border-0 border-none shadow-none outline-none', className)}
      data-testid="toggle-group"
    >
      {options.map(option => {
        const isSelected = Array.isArray(value) && value.includes(option.value);

        return (
          <ToggleGroupItem
            key={option.value}
            value={option.value}
            className={cn(
              'flex justify-between items-center w-full bg-background p-3',
              'hover:bg-background',
              'data-[state=on]:bg-background',
              '!m-0 !shadow-none  border-border rounded-md !border-1',
              'data-[state=on]:border-foreground',
              'hover:border-border',
              'outline-none focus:outline-none focus-visible:outline-none',
              hasError ? 'border-error-foreground hover:border-error-foreground data-[state=on]:border-error-foreground' : ''
            )}
            aria-label={option.label}
            {...option.trackingProps}
            data-track-selected={isSelected ? 'Yes' : 'No'}
            data-testid={`${option.value}-item`}
          >
            <div className="flex items-center gap-2">
              <span className="text-foreground shrink-0 [&_svg]:size-6">{option.icon}</span>
              <span className="paragraph-explanatory-strong text-foreground">{option.label}</span>
            </div>

            <CheckboxDisplay checked={isSelected} helpMessageType={hasError ? 'error' : undefined} />
          </ToggleGroupItem>
        );
      })}
    </BaseToggle>
  );
});

ToggleGroup.displayName = 'ToggleGroup';

export { ToggleGroup, type ToggleOption };
